'use client';

import { forwardRef } from 'react';

import NextImage, { ImageProps as NextImageProps } from 'next/image';

import { cloudflareLoader } from '@lib';
import { imageMetaConverter } from '@lib/server';
import { APIImageType, TransformedImageType } from '@types';

export interface SafeImageProps extends Omit<NextImageProps, 'src' | 'alt'> {
  src: APIImageType | APIImageType[] | undefined;
  alt?: string;
  metaProps?: Partial<TransformedImageType>;
  includeDimensions?: boolean;
}

/**
 * SafeImage component wraps the Next.js Image component,
 * calls imageMetaConverter to transform the image and ensures that the src is valid before rendering.
 * If the src is invalid (null, undefined, or empty), it renders nothing or a fallback.
 */
export const SafeImage = forwardRef<HTMLImageElement, SafeImageProps>(
  ({ src, metaProps = {}, includeDimensions = true, ...props }, ref) => {
    // Call imageMetaConverter to transform the image and retrieve the props
    // images cannot have both fill and width values, this prioritises fill
    const imageProps = imageMetaConverter(src, metaProps, {
      includeDimensions: !props.fill && includeDimensions,
    });

    // Check if imageProps and src are valid (not null, undefined, or empty)
    if (!imageProps || !imageProps.src) {
      return null; // Render nothing if the src is invalid (we can swap this out with a fallback if needed)
    }

    if (imageProps.src.includes('24-social.com')) {
      // Use our custom loader if the image is hosted by us
      return <NextImage ref={ref} loader={cloudflareLoader} {...imageProps} {...props} />;
    }

    // Use the native nextjs image optimisation for anything else
    return <NextImage ref={ref} {...imageProps} {...props} />;
  },
);

SafeImage.displayName = 'SafeImage';
